import React, {useContext, useEffect, useState} from 'react';
import moment from 'moment-timezone';
import {List, Map} from 'immutable';

import {pluralize} from 'lib/stringUtils';

import notifier from '@albert-io/notifier';
import {genericMandarkRequest} from 'resources/mandark.resource';

import {Banner, Button, Dialogue, LoadingSpinner, Text} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';

import HardCapWarningBanner from 'client/components/HardCapWarningBanner/HardCapWarningBanner.react';
import errorCodes from 'client/errorCodes';
import CollapsibleList from 'client/components/CollapsibleList/CollapsibleList.react';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';

import masqueradeStore from 'client/generic/Masquerade/Masquerade.store';
import systemTimeOffsetStore from 'generic/SystemTimeOffset/SystemTimeOffset.store';

interface RosterContextValue {
  classroom: {getId: () => string; getName: () => string};
  queries: {
    invitations: {
      getResourcePromise: () => Promise<unknown>;
      getResourceMetadata: () => {getIn: (path: [string, string]) => number};
    };
  };
}

interface DialoguePassthrough {
  className?: string;
  inModal?: boolean;
}

/** Immutable's empty List is a singleton, so hoisting it keeps the initializer off every render. */
const EMPTY_LIST = List();

interface Props extends DialoguePassthrough {
  onCancel?: () => void;
  onConfirm?: () => void;
  seatsRemaining?: number;
}

export default function BulkResendInvitations({
  onConfirm,
  onCancel,
  seatsRemaining,
  ...rest
}: Props) {
  const {classroom, queries} = useContext(ManageRosterContext) as unknown as RosterContextValue;
  const [fulfilledInvitations, setFulfilledInvitations] = useState<any>(EMPTY_LIST);
  const [rejectedInvitations, setRejectedInvitations] = useState<any>(EMPTY_LIST);
  const [resendableInvitations, setResendableInvitations] = useState<List<unknown>>(EMPTY_LIST);
  const [isReady, setIsReady] = useState(false);
  const [isRequestInflight, setIsRequestInflight] = useState(false);

  const fetchClassroomWithResendableInvitations = () => {
    const currentTime = moment(systemTimeOffsetStore.getCurrentTime());
    return (
      resource('classroom_v1')
        .mandarkEndpoint(['classrooms_v1', classroom.getId()])
        // We access the invitations as an included resource
        // on classroom to skirt the 250 limit on page size.
        .include('classroom_invitations_v1')
        .filter({
          included: {
            classroom_invitations_v1: {
              accepted_at: {null: true},
              sent_email_at: {
                less_than_inclusive: currentTime.subtract(1, 'day').valueOf()
              }
            }
          }
        })
        .getResourcePromise()
    );
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // We use the results of this query to ascertain the total number of classroom invitations.
      // This is necessary because we can't assume this query will have resolved in every instance
      // we are using this context object.
      await queries.invitations.getResourcePromise();

      // This query isolates only the invitations we can re-send according to business logic
      const fetchedClassroom = await fetchClassroomWithResendableInvitations();
      if (cancelled) {
        return;
      }
      setIsReady(true);
      setResendableInvitations((fetchedClassroom as any).getClassroomInvitations());
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resendInvitations = async () => {
    setIsRequestInflight(true);

    const classroomId = classroom.getId();
    const teacherId = masqueradeStore.getUserIdByMasqueradeState();

    const payload = (resendableInvitations.toJS() as Array<{email: string}>).map((invitation) => ({
      email: invitation.email,
      issuer_id: teacherId,
      classroom_id: classroomId
    }));

    const parsedResponse: {
      fulfilled: Array<Map<string, unknown>>;
      rejected: Array<Map<string, unknown>>;
    } = {
      fulfilled: [],
      rejected: []
    };

    try {
      const response = await genericMandarkRequest(
        'post',
        {
          resourcePath: ['json', 'classroom_invitations', 'reinvite', 'bulk']
        },
        {
          data: {
            attributes: payload
          }
        }
      );
      (response!.toJS() as Array<{code?: string; email: string; detail?: string}>).forEach(
        (res) => {
          if (res.code) {
            parsedResponse.rejected.push(
              Map({
                status: 'rejected',
                email: res.email,
                reason: res.detail
              })
            );
          } else {
            parsedResponse.fulfilled.push(
              Map({
                status: 'fulfilled',
                email: res.email,
                response: res
              })
            );
          }
        }
      );
    } catch (error) {
      payload.forEach((invitation) =>
        parsedResponse.rejected.push(
          Map({
            status: 'rejected',
            email: invitation.email,
            reason: error
          })
        )
      );
      notifier.notify(error, {
        message: 'Failed to bulk re-send classroom invitations',
        tags: 'BulkResendInvitations'
      });
    }

    setFulfilledInvitations(parsedResponse.fulfilled);
    setRejectedInvitations(parsedResponse.rejected);
    setIsRequestInflight(false);
  };

  const showSummary =
    (!isRequestInflight && fulfilledInvitations.size !== 0) || rejectedInvitations.size !== 0;

  // Modal steps. The order here does not reflect the order in which the user advances.
  // Rather, this works as a sort of waterfall of states in which they can land.

  // Summary of completed re-invitations, sorted by successes and failures
  if (showSummary) {
    return (
      <BulkResendSummary
        fulfilledInvitations={fulfilledInvitations}
        onConfirm={onConfirm}
        rejectedInvitations={rejectedInvitations}
        seatsRemaining={seatsRemaining}
        {...rest}
      />
    );
  }

  // Initial loading state
  if (!isRequestInflight && !isReady) {
    return (
      <Dialogue title='Re-send Invitations' handleClose={onCancel} {...rest}>
        <Dialogue.Body>
          <LoadingSpinner size={2} className='mb-x4' />
        </Dialogue.Body>
      </Dialogue>
    );
  }

  // Discourage user from closing window mid-flight
  if (isRequestInflight) {
    return (
      <Dialogue handleClose={onConfirm} title='Re-send Invitations' {...rest}>
        <Dialogue.Body>
          <LoadingSpinner size={2} className='mb-x4' />
          <Text as='p' className='mb-x2 text-center'>
            We&apos;re saving your changes &mdash; please do not close this window.
          </Text>
        </Dialogue.Body>
      </Dialogue>
    );
  }

  if (!isRequestInflight && isReady && resendableInvitations.isEmpty()) {
    return <NoStaleInvitationsPrompt onCancel={onCancel} {...rest} />;
  }

  return (
    <ResendInvitationsPrompt
      resendableInvitations={resendableInvitations}
      onCancel={onCancel}
      onConfirm={resendInvitations}
      {...rest}
    />
  );
}

interface ResendInvitationsPromptProps extends DialoguePassthrough {
  resendableInvitations: List<unknown>;
  onCancel?: () => void;
  onConfirm?: () => void;
}

const ResendInvitationsPrompt = ({
  resendableInvitations,
  onCancel,
  onConfirm,
  ...rest
}: ResendInvitationsPromptProps) => {
  const {queries, classroom} = useContext(ManageRosterContext) as unknown as RosterContextValue;
  const invitationsTotal = queries.invitations
    .getResourceMetadata()
    .getIn(['page', 'total_entries']);
  return (
    <Dialogue groupTitle title='Re-send Invitations' handleClose={onCancel} {...rest}>
      <Dialogue.Body>
        <Text color='secondary'>for {classroom.getName()}</Text>
        <br />
        <br />
        {resendableInvitations.size < invitationsTotal && (
          <Text as='p'>
            Some invitations were sent within 24 hours and will not be re-sent.
            <br />
            You can only re-send invitations every 24 hours.
          </Text>
        )}
        <Text as='p' className='mb-x0'>
          {`Would you like to re-invite ${resendableInvitations.size} ${pluralize(
            'student',
            resendableInvitations.size
          )}?`}
        </Text>
      </Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button color='secondary' onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onConfirm}>Resend</Button>
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};

interface NoStaleInvitationsPromptProps extends DialoguePassthrough {
  onCancel?: () => void;
}

const NoStaleInvitationsPrompt = ({onCancel, ...rest}: NoStaleInvitationsPromptProps) => (
  <Dialogue title='Re-send Invitations' handleClose={onCancel} {...rest}>
    <Dialogue.Body>
      <Text as='p'>You can only re-send invitations every 24 hours.</Text>
      <Text as='p' className='mb-x0'>
        When invitations become available to re-send, they will appear here.
      </Text>
    </Dialogue.Body>
    <Dialogue.BtnGroup>
      <Button color='secondary' onClick={onCancel}>
        Close
      </Button>
    </Dialogue.BtnGroup>
  </Dialogue>
);

interface BulkResendSummaryProps extends DialoguePassthrough {
  fulfilledInvitations: any;
  onConfirm?: () => void;
  rejectedInvitations: any;
  seatsRemaining?: number;
}

const BulkResendSummary = ({
  fulfilledInvitations,
  onConfirm,
  rejectedInvitations,
  seatsRemaining,
  ...rest
}: BulkResendSummaryProps) => {
  const sortedRejections = React.useMemo(
    () =>
      rejectedInvitations.reduce(
        (acc: Map<string, List<Map<string, unknown>>>, curr: Map<string, unknown>) => {
          const error = curr.get('reason') as {response: {body: {errors: Array<{code: string}>}}};
          const errorCode = error.response.body.errors[0].code;
          if (errorCode === errorCodes.BAD_REQUEST.VALIDATION_ERROR.INVALID_EMAIL_DOMAIN) {
            return acc.update('invalidDomains', (items) => items!.push(curr));
          }
          if (errorCode === errorCodes.FORBIDDEN.EXCEEDED_HARD_CAP_LIMIT) {
            return acc.update('exceededLimits', (items) => items!.push(curr));
          }
          return acc.update('other', (items) => items!.push(curr));
        },
        Map({
          invalidDomains: List(),
          exceededLimits: List(),
          other: List()
        })
      ),
    [rejectedInvitations]
  );

  return (
    <Dialogue title='Confirmation' handleClose={onConfirm} {...rest}>
      <Dialogue.Body>
        {fulfilledInvitations.length !== 0 && (
          <>
            <Banner className='mb-x1' type='positive'>
              Invitations were re-sent to the following recipients:
            </Banner>
            <CollapsibleList
              className='mt-x0 mb-x1 p-x0'
              items={fulfilledInvitations.map((item: Map<string, unknown>) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
        {sortedRejections.get('invalidDomains').size !== 0 && (
          <>
            <Banner className='mt-x3 mb-x1' type='negative'>
              <span>
                The following addresses belong to an email domain that is restricted by your school.{' '}
                <strong>Invitations could not be sent</strong>:
              </span>
            </Banner>
            <CollapsibleList
              className='mt-x0 mb-x1 p-x0'
              items={sortedRejections
                .get('invalidDomains')
                .map((item: Map<string, unknown>) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
        {sortedRejections.get('exceededLimits').size !== 0 && (
          <>
            <HardCapWarningBanner seatsRemaining={seatsRemaining as number} />
            <strong>The following invitations could not be re-sent</strong>:
            <CollapsibleList
              className='mt-x0 mb-x1 p-x0'
              items={sortedRejections
                .get('exceededLimits')
                .map((item: Map<string, unknown>) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
        {sortedRejections.get('other').size !== 0 && (
          <>
            <Banner className='mt-x3 mb-x1' type='negative' dismissable={false}>
              We encountered a problem sending invitations to the following recipients. Please try
              again:
            </Banner>
            <CollapsibleList
              className='mt-x0 mb-x1 p-x0'
              items={sortedRejections
                .get('other')
                .map((item: Map<string, unknown>) => item.get('email'))}
              maxVisibleItems={10}
            />
          </>
        )}
      </Dialogue.Body>
      <Dialogue.BtnGroup>
        <Button color='secondary' onClick={onConfirm}>
          Close
        </Button>
      </Dialogue.BtnGroup>
    </Dialogue>
  );
};
